import { Injectable, OnDestroy } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { IDictionary } from 'src/app/api/modules/core/components/abstract/IDictionary';
import { DefaultDictionary, resolveDictionary, ResolvedDictionary } from 'src/app/app-dictionary';
import { ElfWrite } from 'src/app/util/ElfWrite';

interface DictionaryModel {
  dictionary: ResolvedDictionary;
}

/**
 * The Default State
 *
 * Starts on the built-in English copy rather than empty, because the failures this wording exists
 * for include the ones where the initialize response never arrives.
 */
function initialState(): DictionaryModel {
  return {
    dictionary: DefaultDictionary,
  };
}

/**
 * Stores the system copy the app shows when it has to speak to the player itself.
 */
@Injectable({
  providedIn: 'root',
})
export class DictionaryRepository implements OnDestroy {
  /**
   * The store.
   */
  private store = createStore(
    {
      name: `dictionary`,
    },
    withProps<DictionaryModel>(initialState()),
  );

  /**
   * The current copy.
   */
  dictionary$ = this.store.pipe(select((state) => state.dictionary));

  ////////////////////////////////////////////////////////////////////
  // INITIALIZE
  ////////////////////////////////////////////////////////////////////

  /**
   * Lays the server's copy over the defaults.
   *
   * @param dictionary - the dictionary from the initialize response.
   */
  applyDictionary(dictionary?: Partial<IDictionary>) {
    this.store.update(
      ElfWrite((state) => {
        state.dictionary = resolveDictionary(dictionary);
      }),
    );
  }

  ////////////////////////////////////////////////////////////////////
  // READ
  ////////////////////////////////////////////////////////////////////

  /**
   * The current copy, read synchronously.
   *
   * The error handler needs the wording at the moment it is handed a failure, which is not a
   * point at which it can wait on an observable.
   */
  getDictionary(): ResolvedDictionary {
    return this.store.getValue().dictionary;
  }

  /**
   * Lifecycle Hook
   */
  ngOnDestroy() {
    this.store?.destroy();
  }
}
